/**
 * Calcolo del colore di testo a contrasto WCAG su un dato colore di sfondo.
 *
 * WHY (funzione pura condivisa): la logica nasce nei metodi privati della
 * directive `SiaColorContrastAutoDirective`. E' stata estratta qui come
 * funzione pura per essere riusata sia dalla directive (DOM-driven) sia dai
 * widget dashboard ECharts, che NON hanno un elemento DOM da osservare ma
 * conoscono il colore di fill della forma e devono colorare la label interna
 * con un colore leggibile. L'algoritmo e' identico a quello della directive:
 * ogni cambiamento qui si riflette su entrambi i consumer.
 */

/**
 * Modalita' di derivazione del colore di testo a contrasto.
 *
 * - `auto`: bianco/nero secco a soglia luminanza 0.179 (WCAG, robusto).
 * - `shade`: scurisce la tinta dello sfondo (stessa famiglia cromatica).
 * - `tint`: schiarisce la tinta dello sfondo.
 * - `adaptive`: se lo sfondo e' scuro schiarisce, se chiaro scurisce, con
 *   binary search per garantire `minContrastRatio`.
 */
export type SiaContrastMode = 'auto' | 'shade' | 'tint' | 'adaptive';

/**
 * Ritorna un colore hex (`#rrggbb`) di testo leggibile sul colore `bgColor`.
 *
 * @param bgColor colore di sfondo: hex (`#rgb`/`#rrggbb`) o stringa `rgb(...)`
 *   (la directive passa il computed style, che e' in forma `rgb(r, g, b)`).
 * @param mode strategia di contrasto (default `'auto'`).
 * @param minContrastRatio rapporto di contrasto minimo da garantire nelle
 *   modalita' che lo enforcano (`shade`/`tint`/`adaptive`). Default 3.0
 *   (WCAG AA per testo grande).
 */
export function getContrastingTextColor(
  bgColor: string,
  mode: SiaContrastMode = 'auto',
  minContrastRatio = 3.0,
): string {
  const hexColor = rgbToHex(parseColor(bgColor));
  const luminance = calculateLuminance(bgColor);

  switch (mode) {
    case 'shade':
      return '#' + shadeColor(hexColor, minContrastRatio);
    case 'tint':
      return '#' + tintColor(hexColor, minContrastRatio);
    case 'adaptive':
      // Se lo sfondo e' scuro lo schiariamo; se chiaro lo scuriamo.
      return luminance <= 0.179
        ? '#' + tintColor(hexColor, minContrastRatio)
        : '#' + shadeColor(hexColor, minContrastRatio);
    case 'auto':
    default:
      return luminance > 0.179 ? '#000000' : '#ffffff';
  }
}

/**
 * Ritorna `true` se il colore di sfondo passato e' effettivamente trasparente
 * (alpha 0) oppure non risolvibile a un colore opaco reale (`transparent`,
 * stringa vuota, `none`).
 *
 * WHY: `getComputedStyle().backgroundColor` su un elemento senza sfondo
 * proprio ritorna `rgba(0, 0, 0, 0)`. Interpretarlo come nero porterebbe a
 * scrivere testo bianco a contrasto, sovrascrivendo il colore ereditato dal
 * tema. I consumer (es. la directive) usano questa guardia per NON applicare
 * alcun colore quando lo sfondo e' di fatto assente.
 *
 * @param colorStr valore di colore (computed style, hex, `rgb`/`rgba`, keyword).
 */
export function isTransparentBackground(colorStr: string): boolean {
  if (!colorStr) {
    return true;
  }
  const normalized = colorStr.trim().toLowerCase();
  if (normalized === 'transparent' || normalized === 'none') {
    return true;
  }
  // rgba(r, g, b, a) / rgb(... / 0) — estrae l'eventuale componente alpha.
  const rgbaMatch = normalized.match(/rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*(?:,\s*([\d.]+)\s*)?\)/);
  if (rgbaMatch && rgbaMatch[1] !== undefined) {
    return parseFloat(rgbaMatch[1]) === 0;
  }
  // hex a 8 cifre (#rrggbbaa): alpha negli ultimi 2 caratteri.
  const hex8 = normalized.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/);
  if (hex8) {
    return parseInt(hex8[4], 16) === 0;
  }
  return false;
}

function calculateLuminance(color: string): number {
  const rgb = parseColor(color);
  const [r, g, b] = rgb.map((c) => {
    const normalized = c / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function parseColor(colorStr: string): number[] {
  if (colorStr.startsWith('#')) {
    return hexToRgb(colorStr);
  }
  const rgbMatch = colorStr.match(/(\d+),\s*(\d+),\s*(\d+)/);
  return rgbMatch ? [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])] : [0, 0, 0];
}

function hexToRgb(hex: string): number[] {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
}

/** Converte un array RGB in hex (senza `#`). */
function rgbToHex(rgb: number[]): string {
  return rgb
    .map((x) => {
      const hex = Math.max(0, Math.min(255, x)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('');
}

/** Scurisce un colore (stessa famiglia) garantendo il contrasto minimo. */
function shadeColor(hexcolor: string, minContrastRatio: number): string {
  hexcolor = hexcolor.replace('#', '');

  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);

  const hsl = rgbToHsl(r, g, b);
  const bgLuminance = relativeLuminance([r, g, b]);

  let newHue = hsl[0];
  let newSaturation = hsl[1];
  if (hsl[1] < 0.1) {
    newHue = 0;
    newSaturation = 0;
  } else {
    newSaturation = Math.min(1, hsl[1] * 1.5);
  }

  let newLightness = Math.max(0.1, hsl[2] * 0.4);

  let rgb = hslToRgb(newHue, newSaturation, newLightness);
  let rgbNormalized = rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))));
  let textLuminance = relativeLuminance(rgbNormalized);

  let contrastRatio = calculateContrastRatio(bgLuminance, textLuminance);

  if (contrastRatio < minContrastRatio) {
    let minL = 0;
    let maxL = Math.min(hsl[2], 0.5);

    for (let attempts = 0; attempts < 8 && contrastRatio < minContrastRatio; attempts++) {
      newLightness = (minL + maxL) / 2;

      rgb = hslToRgb(newHue, newSaturation, newLightness);
      rgbNormalized = rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))));
      textLuminance = relativeLuminance(rgbNormalized);
      contrastRatio = calculateContrastRatio(bgLuminance, textLuminance);

      if (contrastRatio >= minContrastRatio) {
        minL = newLightness;
      } else {
        maxL = newLightness;
      }
    }

    if (contrastRatio < minContrastRatio) {
      return '000000';
    }
  }

  rgb = hslToRgb(newHue, newSaturation, newLightness);

  return rgb
    .map((v) => {
      const colorValue = Math.max(0, Math.min(255, Math.round(v)));
      return colorValue.toString(16).padStart(2, '0');
    })
    .join('');
}

/** Schiarisce un colore (stessa famiglia) garantendo il contrasto minimo. */
function tintColor(hexcolor: string, minContrastRatio: number): string {
  hexcolor = hexcolor.replace('#', '');

  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);

  const hsl = rgbToHsl(r, g, b);
  const bgLuminance = relativeLuminance([r, g, b]);

  let newHue = hsl[0];
  let newSaturation = hsl[1];
  if (hsl[1] < 0.1) {
    newHue = 0;
    newSaturation = 0;
  } else {
    newSaturation = Math.min(1, hsl[1] * 1.5);
  }

  let newLightness = Math.min(0.9, 0.6 + hsl[2] * 0.4);

  let rgb = hslToRgb(newHue, newSaturation, newLightness);
  let rgbNormalized = rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))));
  let textLuminance = relativeLuminance(rgbNormalized);

  let contrastRatio = calculateContrastRatio(bgLuminance, textLuminance);

  if (contrastRatio < minContrastRatio) {
    let minL = Math.max(hsl[2], 0.5);
    let maxL = 1;

    for (let attempts = 0; attempts < 8 && contrastRatio < minContrastRatio; attempts++) {
      newLightness = (minL + maxL) / 2;

      rgb = hslToRgb(newHue, newSaturation, newLightness);
      rgbNormalized = rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))));
      textLuminance = relativeLuminance(rgbNormalized);
      contrastRatio = calculateContrastRatio(bgLuminance, textLuminance);

      if (contrastRatio >= minContrastRatio) {
        maxL = newLightness;
      } else {
        minL = newLightness;
      }
    }

    if (contrastRatio < minContrastRatio) {
      return 'ffffff';
    }
  }

  rgb = hslToRgb(newHue, newSaturation, newLightness);

  return rgb
    .map((v) => {
      const colorValue = Math.max(0, Math.min(255, Math.round(v)));
      return colorValue.toString(16).padStart(2, '0');
    })
    .join('');
}

/** Luminanza relativa di un colore RGB (formula WCAG). */
function relativeLuminance(rgb: number[]): number {
  const [r, g, b] = rgb.map((v) => {
    const normalized = v / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Rapporto di contrasto tra due luminanze (formula WCAG). */
function calculateContrastRatio(luminance1: number, luminance2: number): number {
  const lighter = Math.max(luminance1, luminance2);
  const darker = Math.min(luminance1, luminance2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Converte RGB in HSL. */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return [h, s, l];
}

/** Converte HSL in RGB. */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [r * 255, g * 255, b * 255];
}
